import { getUtmShareUrl } from '@/app/utils/urlHelper';
import JobClientDetails from '@/components/JobClientDetails';
import { JobInformation } from '@/components/JobInformation';
import ShareContainer from '@/components/ShareContainer';
import { useAuth0User } from '@/modules/Context/useAuth0User';
import { IJobDetail } from '@/types/jobs';
import { generateJobDetailsURL } from '@/utils/jobListingHelper';
import { useAnalytics } from '@/utils/useAnalytics';
import cx from 'classnames';
import Link from 'next/link';
import React, { ReactElement } from 'react';

import { Theme, Typography, useMediaQuery } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';

import styles from './JobCard.module.scss';

/**
 * JobCard displays the job information in a card layout.
 * It accepts 2 optional elements (topButtonElement and bottomButtonElement) we passed these two elements
 * if we want to have some action button displayed to the job card e.g (withdraw button, enquire button, etc.)
 * @param data - Job details data
 * @param topButtonElement - Element that we want to display at the top-right corner of the job card
 * @param bottomButtonElement - Element that we want to display at the bottom-right corner of the job card
 * @param user - User details
 * @param className - Additional class we want to apply in the main card
 */
interface JobCardProps {
  data: IJobDetail;
  topButtonElement?: ReactElement;
  bottomButtonElement?: ReactElement;
  className?: string;
  isJobSummary?: boolean;
  isHighlighted?: boolean;
}

const JobCard = ({
  data,
  topButtonElement,
  bottomButtonElement,
  className,
  isJobSummary = false,
  isHighlighted = false,
}: JobCardProps) => {
  const track = useAnalytics();
  const { user } = useAuth0User();
  const isExtraSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));
  const { jobNumber } = data;
  const subspcialityOrSpeciality = data.subspeciality ? data.subspeciality : data.speciality;
  const jobDetailsPath = generateJobDetailsURL(
    data.jobNumber,
    data.grade,
    subspcialityOrSpeciality,
  );
  const jobSharingUrl = `${globalThis.location?.origin}${jobDetailsPath}`;
  const newLineEncoding = '%0d%0a';

  return (
    <article
      className={cx(className, styles.container, isHighlighted && styles.highlighted)}
      data-testid="job-card"
    >
      <Grid container alignItems="center">
        <Grid item xs={9} sm={6} md={6}>
          <p data-testid="job-card-grade" className={styles.grade}>
            {data.grade}
          </p>
        </Grid>
        <Grid item container justifyContent="flex-end" md={6} sm={6} xs={3}>
          {topButtonElement}
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={12} sm={6} md={5} lg={6}>
          <Link prefetch={false} href={jobDetailsPath} as={jobDetailsPath} passHref>
            {/* eslint-disable */}
            <a
              className={styles.title}
              onClick={() => {
                track('Job Details', {
                  'Details Origin': 'Job Card',
                  'Details Action': 'Title Link',
                  jobNumber: jobNumber,
                });
              }}
            >
              {/* eslint-enable */}
              <Typography
                variant="h5"
                component={isJobSummary ? 'h2' : 'h1'}
                data-testid="job-card-speciality"
              >
                {data.subspeciality ? data.subspeciality : data.speciality}
              </Typography>
            </a>
          </Link>
          <JobInformation job={data} />
        </Grid>

        <Grid item container justifyContent="space-between" xs={12} sm={6} md={7} lg={6}>
          <JobClientDetails user={user} data={data} />
          <Grid
            item
            container
            justifyContent={isExtraSmallScreen ? 'space-between' : 'flex-end'}
            alignItems="flex-end"
            className={styles.bottomContainer}
          >
            <Grid item>
              <ShareContainer
                jobOrArticleShare="jobshare"
                id={jobNumber}
                className={styles.jobShare}
                emailTitle={
                  <>
                    Who would you like <br /> to send this job to?
                  </>
                }
                emailSubject={`${user?.given_name ?? 'Medrecruit'} recommended a job for you`}
                emailBody={`View the job recommended for you by ${
                  user?.given_name ?? 'Medrecruit'
                } ${newLineEncoding}${encodeURI(
                  getUtmShareUrl('jobshare', jobSharingUrl, 'email'),
                )}`}
                facebookTitle={data.title}
                shareUrl={jobSharingUrl}
                shareTrackingProps={{
                  name: 'Share Job',
                  details: {
                    'Share Origin': 'Job Card',
                    jobNumber: jobNumber,
                  },
                }}
                popperPlacement={isExtraSmallScreen ? 'bottom-start' : 'bottom-end'}
              />
            </Grid>
            <Grid item>{bottomButtonElement}</Grid>
          </Grid>
        </Grid>
      </Grid>
    </article>
  );
};

export default JobCard;
